import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, ChevronLeft, ChevronRight, Flower2, GraduationCap, Info, Leaf, MapPin, Snowflake, Sun, TreePine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/api";
import { ASSO, displayEventDate } from "@/constants";
import { DATES_SCOLAIRES, JOURS_FERIES, VACANCES, vacanceForDay } from "@/constants/holidays";
import ParticipationDialog from "@/components/ParticipationDialog";
import { isPastEvent } from "@/components/PastEventStamp";

const VAC_ICONS = { toussaint: Leaf, noel: TreePine, hiver: Snowflake, printemps: Flower2, ete: Sun };
const VAC_CARD = {
  toussaint: "bg-orange-50 border-orange-200 text-orange-800",
  noel: "bg-rose-50 border-rose-200 text-rose-800",
  hiver: "bg-sky-50 border-sky-200 text-sky-800",
  printemps: "bg-emerald-50 border-emerald-200 text-emerald-800",
  ete: "bg-amber-50 border-amber-200 text-amber-800",
};

const LEGEND = [
  { key: "cetf", label: "Événements CETF", dot: "bg-pink-500" },
  { key: "vacances", label: "Vacances scolaires (Zone B)", dot: "bg-sky-400" },
  { key: "feries", label: "Jours fériés", dot: "bg-emerald-500" },
  { key: "scolaire", label: "Dates scolaires importantes", dot: "bg-amber-500" },
];

const WEEKDAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const pad = (n) => String(n).padStart(2, "0");
const isoDay = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
const todayIso = () => { const t = new Date(); return isoDay(t.getFullYear(), t.getMonth(), t.getDate()); };
const fmtDay = (iso, withYear = true) =>
  new Date(iso + "T12:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", ...(withYear ? { year: "numeric" } : {}) });

export default function EventCalendar() {
  const [cursor, setCursor] = useState(() => { const t = new Date(); return { y: t.getFullYear(), m: t.getMonth() }; });
  const [events, setEvents] = useState([]);
  const [filters, setFilters] = useState({ cetf: true, vacances: true, feries: true, scolaire: true });
  const [selected, setSelected] = useState(null);
  const [participate, setParticipate] = useState(false);

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  const eventsByDay = useMemo(() => {
    const map = {};
    for (const e of events) {
      const d = (e.date || "").slice(0, 10);
      if (d) (map[d] = map[d] || []).push(e);
    }
    return map;
  }, [events]);

  const ferieByDay = useMemo(() => Object.fromEntries(JOURS_FERIES.map((f) => [f.date, f.name])), []);
  const scolaireByDay = useMemo(() => Object.fromEntries(DATES_SCOLAIRES.map((s) => [s.date, s.name])), []);

  const { y, m } = cursor;
  const offset = (new Date(y, m, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const cells = [...Array(offset).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  while (cells.length % 7 !== 0) cells.push(null);

  const monthLabel = new Date(y, m, 1).toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  const nav = (delta) => setCursor((c) => { const d = new Date(c.y, c.m + delta, 1); return { y: d.getFullYear(), m: d.getMonth() }; });
  const goToday = () => { const t = new Date(); setCursor({ y: t.getFullYear(), m: t.getMonth() }); };
  const today = todayIso();

  const upcomingVacances = VACANCES.filter((v) => (v.reprise || v.start) >= today);

  return (
    <div data-testid="event-calendar" className="space-y-8">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2" data-testid="cal-legend">
        {LEGEND.map((l) => (
          <label key={l.key} className="flex items-center gap-2 text-xs sm:text-sm text-slate-600 cursor-pointer" data-testid={`cal-filter-${l.key}`}>
            <Checkbox checked={filters[l.key]} onCheckedChange={(v) => setFilters((f) => ({ ...f, [l.key]: v === true }))} />
            <span className={`h-2.5 w-2.5 rounded-full ${l.dot}`} aria-hidden="true" />
            {l.label}
          </label>
        ))}
      </div>

      <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-slate-100">
          <Button onClick={() => nav(-1)} data-testid="cal-prev-btn" aria-label="Mois précédent"
            className="h-11 w-11 p-0 rounded-full bg-emerald-600 text-white shadow-[0_4px_0_#065f46,0_8px_16px_rgba(5,150,105,0.35)] hover:bg-emerald-500 hover:shadow-[0_2px_0_#065f46,0_5px_12px_rgba(5,150,105,0.35)] hover:translate-y-0.5 active:translate-y-1 active:shadow-[0_0_0_#065f46,0_2px_6px_rgba(5,150,105,0.3)] transition-all">
            <ChevronLeft className="h-6 w-6" strokeWidth={3} />
          </Button>
          <div className="flex items-center gap-3">
            <h3 className="text-lg sm:text-xl font-bold text-slate-800 capitalize" data-testid="cal-month-label">{monthLabel}</h3>
            <Button variant="ghost" size="sm" className="text-sky-700" onClick={goToday} data-testid="cal-today-btn">Aujourd'hui</Button>
          </div>
          <Button onClick={() => nav(1)} data-testid="cal-next-btn" aria-label="Mois suivant"
            className="h-11 w-11 p-0 rounded-full bg-emerald-600 text-white shadow-[0_4px_0_#065f46,0_8px_16px_rgba(5,150,105,0.35)] hover:bg-emerald-500 hover:shadow-[0_2px_0_#065f46,0_5px_12px_rgba(5,150,105,0.35)] hover:translate-y-0.5 active:translate-y-1 active:shadow-[0_0_0_#065f46,0_2px_6px_rgba(5,150,105,0.3)] transition-all">
            <ChevronRight className="h-6 w-6" strokeWidth={3} />
          </Button>
        </div>

        <div className="overflow-x-auto">
          <div className="relative min-w-[700px]">
            <img src={ASSO.logoTreeUrl} alt="" aria-hidden="true"
              className="absolute inset-0 m-auto h-64 w-auto opacity-[0.05] pointer-events-none select-none" />
            <div className="grid grid-cols-7 text-center text-[11px] font-semibold uppercase tracking-wider text-slate-400 border-b border-slate-100">
              {WEEKDAYS.map((d) => <div key={d} className="py-2.5">{d}</div>)}
            </div>
            <div className="grid grid-cols-7" data-testid="cal-grid">
              {cells.map((d, i) => {
                if (d === null) return <div key={`empty-${i}`} className="min-h-20 sm:min-h-24 bg-slate-50/60" />;
                const iso = isoDay(y, m, d);
                const vac = filters.vacances ? vacanceForDay(iso) : null;
                const ferie = filters.feries ? ferieByDay[iso] : null;
                const scol = filters.scolaire ? scolaireByDay[iso] : null;
                const evts = filters.cetf ? eventsByDay[iso] || [] : [];
                return (
                  <div key={iso} data-testid={`cal-day-${iso}`}
                    className={`relative min-h-20 sm:min-h-24 border-b border-l border-slate-100 p-1.5 ${vac ? "bg-sky-50/80" : "bg-white"}`}>
                    <div className="flex items-center gap-1.5">
                      <span className={`text-xs sm:text-sm font-semibold inline-flex h-6 w-6 items-center justify-center rounded-full ${
                        iso === today ? "bg-emerald-600 text-white" : "text-slate-600"}`}>{d}</span>
                      {ferie && <span className="h-2 w-2 rounded-full bg-emerald-500 shrink-0" title={ferie} aria-label={ferie} />}
                      {scol && <span className="h-2 w-2 rounded-full bg-amber-500 shrink-0" title={scol} aria-label={scol} />}
                    </div>
                    {vac && vac.start === iso && (
                      <span className="block text-[10px] font-semibold text-sky-700 leading-tight mt-1" data-testid={`cal-vacance-${vac.id}`}>
                        {vac.short}
                      </span>
                    )}
                    {evts.map((e) => {
                      const pastEvt = isPastEvent(e.date);
                      return (
                      <button key={e.id} onClick={() => setSelected(e)} data-testid={`cal-event-${e.id}`}
                        className={`block w-full text-left text-[10px] sm:text-[11px] font-medium rounded-md px-1.5 py-1 mt-1 truncate transition-colors ${
                          pastEvt
                            ? "bg-slate-100 text-slate-400 line-through hover:bg-slate-200"
                            : "bg-pink-100 text-pink-800 hover:bg-pink-200"
                        }`}
                        title={pastEvt ? `${e.title} (événement passé)` : e.title}>
                        {e.title}
                      </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div data-testid="vacances-section">
        <div className="flex items-center gap-3 mb-5">
          <div className="h-11 w-11 rounded-xl bg-sky-100 flex items-center justify-center shrink-0">
            <GraduationCap className="h-5 w-5 text-sky-700" />
          </div>
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-slate-800">Prochaines vacances scolaires</h3>
            <p className="text-xs sm:text-sm text-slate-500">Zone B · Académie d'Orléans-Tours — dates officielles 2026-2027 (Éducation nationale)</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {upcomingVacances.map((v) => {
            const Icon = VAC_ICONS[v.id] || Sun;
            return (
              <div key={v.id} className={`rounded-2xl border p-4 flex items-start gap-3 ${VAC_CARD[v.id]}`} data-testid={`vacation-card-${v.id}`}>
                <Icon className="h-6 w-6 shrink-0 mt-0.5" aria-hidden="true" />
                <div className="min-w-0">
                  <p className="font-semibold text-sm">{v.name}</p>
                  <p className="text-xs mt-1 leading-relaxed opacity-90">
                    {v.reprise
                      ? <>Du {fmtDay(v.start, false)} au {fmtDay(v.reprise)}</>
                      : <>À partir du {fmtDay(v.start)}</>}
                  </p>
                  <p className="text-[11px] mt-1.5 opacity-70">Départ après la classe{v.reprise ? " · reprise le matin" : ""}</p>
                </div>
              </div>
            );
          })}
        </div>
        <p className="flex items-start gap-2 text-xs text-slate-400 mt-4 leading-relaxed">
          <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          Zone B — académie d'Orléans-Tours (concerne notamment l'Eure-et-Loir, 28). Les dates sont données à titre indicatif
          et peuvent être modifiées par l'Éducation nationale.
        </p>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <DialogContent data-testid="event-detail-dialog">
          <DialogHeader>
            <DialogTitle>{selected?.title}</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <p className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-b from-slate-50 to-sky-50 border border-sky-100 px-3 py-1.5 font-semibold text-sky-900 shadow-[0_3px_0_#e0f2fe,0_6px_12px_rgba(2,132,199,0.15)]" data-testid="cal-event-date"><CalendarDays className="h-4 w-4 text-sky-500" />{displayEventDate(selected.date)}</p>
              {isPastEvent(selected.date) && (
                <p className="rounded-xl border-2 border-red-200 bg-red-50 px-4 py-2.5 text-xs font-semibold text-red-700 leading-relaxed" data-testid="cal-event-past-note">
                  Événement passé — merci de votre mobilisation et merci à nos bénévoles.
                </p>
              )}
              {!isPastEvent(selected.date) && selected.registration_open === false && (
                <p className="rounded-xl border-2 border-slate-300 bg-blue-50/70 px-4 py-2.5 text-xs font-semibold text-slate-600 leading-relaxed" data-testid="cal-event-closed-note">
                  Cet événement est désormais complet. L'inscription est clôturée. Merci de votre intérêt. — CETF Association.
                </p>
              )}
              {selected.location && (
                <p className="flex items-center gap-2 text-slate-700"><MapPin className="h-4 w-4 text-slate-400" />{selected.location}</p>
              )}
              {selected.description && <p className="text-slate-600 leading-relaxed whitespace-pre-line">{selected.description}</p>}
              <div className="flex flex-wrap gap-2.5 pt-2">
                {!isPastEvent(selected.date) && selected.registration_open !== false && (
                  <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setParticipate(true)} data-testid="event-detail-participate-btn">
                    Je participe à l'événement
                  </Button>
                )}
                <Button variant="outline" asChild>
                  <Link to="/evenements">Tous les événements</Link>
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <ParticipationDialog event={selected} open={participate} onOpenChange={setParticipate} />
    </div>
  );
}
